import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Field, inputCls } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";
import { useSite } from "@/context/SiteContext";

export const HIRE_FORM_DEFAULTS = {
  eyebrow: "Bridal & Event Hire", title: "Hire Enquiry",
  intro: "Tell us about your occasion and the pieces you love. Our hire team will reply within one business day with availability and pricing.",
  occasions: ["Wedding", "Engagement party", "Gala or ball", "Races", "Red carpet or media", "Photoshoot", "Other"],
  durations: ["1 day", "Weekend (2–3 days)", "Up to a week", "Not sure yet"],
  pieces: ["Necklace", "Earrings", "Bracelet", "Ring", "Tiara or hair piece", "Full suite"],
  budgets: ["Under A$250", "A$250 – A$500", "A$500 – A$1,000", "Over A$1,000", "Not sure"],
  submit_label: "Send Hire Enquiry", success_title: "Thank you",
  success_text: "We've received your hire enquiry. Our team will be in touch within one business day with availability, pricing and next steps.",
};
const tid = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Opt = ({ value, opts, onChange, testId }: any) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
    <option value="">Select</option>{opts.map((o: string) => <option key={o}>{o}</option>)}
  </select>
);

function Pieces({ opts, value, onChange }: { opts: string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div>
      <span className="eyebrow text-neutral-500 block mb-3">Pieces you're interested in</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="he-pieces">
        {opts.map((p) => {
          const on = value.includes(p);
          return <button type="button" key={p} onClick={() => onChange(on ? value.filter((x) => x !== p) : [...value, p])} aria-pressed={on}
            className={`py-3.5 px-3 border text-sm transition-colors ${on ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 hover:border-neutral-500"}`} data-testid={`he-piece-${tid(p)}`}>{p}</button>;
        })}
      </div>
    </div>
  );
}

export default function HireEnquiry() {
  const { site } = useSite();
  const c = { ...HIRE_FORM_DEFAULTS, ...(site?.hire_form || {}) };
  useMeta(`${c.title} | Somnio.Co`, c.intro);
  const today = new Date().toLocaleDateString("en-CA");
  const [f, setF] = useState<any>({ name: "", email: "", phone: "", contact_pref: "Email", occasion: "", event_date: "", duration: "", pieces: [], budget: "", location: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k: string) => (v: any) => setF({ ...f, [k]: typeof v === "string" || Array.isArray(v) ? v : v.target.value });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/hire/enquiries", { ...f, occasion: f.occasion || c.occasions[0] }); setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="max-w-xl mx-auto text-center px-5 py-28" data-testid="hire-enquiry-success">
      <h1 className="font-display text-4xl font-light">{c.success_title}, {f.name.split(" ")[0]}</h1>
      <p className="mt-5 text-neutral-600">{c.success_text}</p>
    </div>
  );
  return (
    <div className="pb-24">
      <PageHeader eyebrow={c.eyebrow} title={c.title} intro={c.intro} />
      <form onSubmit={submit} className="max-w-3xl mx-auto px-5 md:px-12 space-y-6" data-testid="hire-enquiry-form">
        <div className="grid sm:grid-cols-2 gap-6">
          <Field label="Your name"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="he-name" /></Field>
          <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="he-email" /></Field>
          <Field label="Phone (optional)"><input type="tel" value={f.phone} onChange={set("phone")} className={inputCls} data-testid="he-phone" /></Field>
          <Field label="Best way to reach you"><Opt value={f.contact_pref} opts={["Email", "Phone", "Either"]} onChange={set("contact_pref")} testId="he-contact" /></Field>
          <Field label="Occasion"><select required value={f.occasion} onChange={set("occasion")} className={inputCls} data-testid="he-occasion">
            <option value="">Select</option>{c.occasions.map((o: string) => <option key={o}>{o}</option>)}</select></Field>
          <Field label="Event date (optional)"><input type="date" min={today} value={f.event_date} onChange={set("event_date")} className={inputCls} data-testid="he-date" /></Field>
          <Field label="How long do you need the pieces?"><Opt value={f.duration} opts={c.durations} onChange={set("duration")} testId="he-duration" /></Field>
          <Field label="Hire budget (optional)"><Opt value={f.budget} opts={c.budgets} onChange={set("budget")} testId="he-budget" /></Field>
        </div>
        <Pieces opts={c.pieces} value={f.pieces} onChange={set("pieces")} />
        <Field label="Suburb or venue (optional)"><input value={f.location} onChange={set("location")} className={inputCls} data-testid="he-location" /></Field>
        <Field label="Tell us about your look or the pieces you love (optional)"><textarea rows={5} value={f.message} onChange={set("message")} className={inputCls} data-testid="he-message" /></Field>
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" onChange={set("website")} />
        <p className="text-xs text-neutral-600">Your details are kept private and only used by our hire team to contact you.</p>
        <button disabled={busy} className="btn-ink w-full py-4 text-[11px] tracking-[0.25em] uppercase disabled:opacity-60" data-testid="he-submit">{busy ? "Sending…" : c.submit_label}</button>
      </form>
    </div>
  );
}
